// ============================================================
// useCalendarRangeData.ts
// Loads everything the calendar shows for ONE range — no pop-in:
//
//  1. The moment the view / range / scope changes, EVERY source is fired in
//     parallel: tasks + deadlines + appointments (/api/calendar), day-notes,
//     reminders, and Outlook (Graph, via our server). Outlook isn't held back
//     behind the Microsoft status check any more — that runs alongside.
//  2. Until the range is ready the caller shows the grid with a skeleton in
//     every day cell (`loading`).
//  3. Items are revealed TOGETHER. If Outlook is still loading 1.5 s in, the
//     rest is revealed and `outlookLoading` drives a small "Loading Outlook…"
//     marker; Outlook then fades in (`outlookLate`, so the grids keep the
//     other items where they are).
//  4. Ranges already seen are shown instantly from the cache (calendarRange
//     Cache) and refreshed quietly; the previous + next range for the current
//     view are prefetched once a range is on screen.
//  5. A response for a range the user has left is ignored (generation guard),
//     so a stale range's items never appear under the new range's dates.
// ============================================================
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CalendarEntry, DayNote, Reminder } from '../../types/calendar.types'
import { fetchCalendarApi, fetchDayNotesApi, fetchRemindersApi, fetchOutlookEventsApi, msStatusApi } from '../../api/casevoApi'
import type { OutlookEvent } from '../../api/casevoApi'
import {
  CalendarRange,
  CalendarRangeCache,
  CalendarScope,
  CasevoBundle,
  createCalendarRangeCache,
} from './calendarRangeCache'
import type { CalendarFade } from './CalendarSkeleton'

// How long the rest of a range waits for Outlook before showing without it.
export const OUTLOOK_REVEAL_WAIT_MS = 1500

// The app-wide cache (lives across Calendar mounts, owner-scoped).
export const calendarRangeCache: CalendarRangeCache = createCalendarRangeCache({
  fetchCasevo: async (range, scope) => {
    const [entries, notes, reminders] = await Promise.all([
      fetchCalendarApi({ from: range.from, to: range.to, scope }),
      fetchDayNotesApi({ from: range.from, to: range.to }),
      fetchRemindersApi({ from: range.from, to: range.to }),
    ])
    return { entries, notes, reminders }
  },
  fetchOutlook: async (range, { fresh }) => {
    const r = await fetchOutlookEventsApi({ from: range.from, to: range.to, fresh })
    return { connected: r.connected, needsReconnect: !!r.needsReconnect, events: r.events }
  },
})

export interface CalendarRangeOptions {
  range: CalendarRange
  // Identity of what's on screen (view + range). A change starts a new load.
  rangeId: string
  scope: CalendarScope
  // `${firmId}:${userId}` — the cache owner. null while signed out.
  owner: string | null
  // Prefetched once this range is on screen (previous + next for the view).
  neighbours: CalendarRange[]
  // Bumped by "Try again" / a permissions change → bypass the cache.
  reloadNonce: number
  cache?: CalendarRangeCache
  outlookWaitMs?: number
}

export type { CalendarFade }

interface ViewState {
  gen: number
  rangeId: string
  casevo: CasevoBundle | null
  casevoKey: string | null
  casevoRange: CalendarRange | null
  outlook: OutlookEvent[]
  loading: boolean
  outlookLoading: boolean
  outlookLate: boolean
  fromCache: boolean
  error: string | null
}

export interface CalendarRangeData {
  entries: CalendarEntry[] | null
  notes: DayNote[]
  reminders: Reminder[]
  outlookEvents: OutlookEvent[]
  loading: boolean
  error: string | null
  outlookConnected: boolean | null
  outlookNeedsReconnect: boolean
  outlookLoading: boolean
  outlookLate: boolean
  fade: CalendarFade
  setEntries: (fn: (es: CalendarEntry[] | null) => CalendarEntry[] | null) => void
  setReminders: (fn: (rs: Reminder[]) => Reminder[]) => void
  // Quiet refresh of what's on screen (after a write / live change / focus).
  // Marks every cached range stale so other ranges re-validate when shown.
  // `outlook` also re-reads Outlook, skipping the server's 60 s cache (focus /
  // poll) — Casevo writes never change Outlook, so they leave it alone.
  refresh: (opts?: { outlook?: boolean }) => void
}

const LOAD_ERROR = 'Unable to load your calendar. Please try again.'

export const useCalendarRangeData = (opts: CalendarRangeOptions): CalendarRangeData => {
  const cache = opts.cache ?? calendarRangeCache
  const waitMs = opts.outlookWaitMs ?? OUTLOOK_REVEAL_WAIT_MS
  const { range, rangeId, scope, owner, reloadNonce } = opts
  // What's on screen = view + range + scope + owner.
  const ident = `${rangeId}|${scope}|${owner ?? ''}`

  const [view, setView] = useState<ViewState>(() => ({
    gen: 0, rangeId: '', casevo: null, casevoKey: null, casevoRange: null, outlook: [],
    loading: true, outlookLoading: false, outlookLate: false, fromCache: false, error: null,
  }))
  const viewRef = useRef(view)
  viewRef.current = view
  const genRef = useRef(0)
  // null = not known yet (status + first Outlook read both answer it).
  const [outlookConnected, setOutlookConnected] = useState<boolean | null>(null)
  const [outlookNeedsReconnect, setOutlookNeedsReconnect] = useState(false)
  const connectedRef = useRef<boolean | null>(null)
  connectedRef.current = outlookConnected
  const latest = useRef(opts)
  latest.current = opts

  // Microsoft status — in PARALLEL with the first range load (it used to gate
  // the Outlook read, putting it in series). Only steers the header marker +
  // whether later ranges ask Outlook at all.
  useEffect(() => {
    let cancelled = false
    void msStatusApi()
      .then((s) => {
        if (cancelled) return
        if (connectedRef.current === null || !s.connected) setOutlookConnected(s.connected)
        if (s.needsReconnect) setOutlookNeedsReconnect(true)
      })
      .catch(() => { if (!cancelled && connectedRef.current === null) setOutlookConnected(false) })
    return () => { cancelled = true }
  }, [])

  const applyOutlookStatus = useCallback((connected: boolean, needsReconnect: boolean): void => {
    setOutlookNeedsReconnect(needsReconnect)
    setOutlookConnected(connected)
  }, [])

  const nonceRef = useRef(reloadNonce)

  // The range load. A LAYOUT effect so a cached range is committed before the
  // browser paints the new dates (no one-frame skeleton or stale flash).
  useLayoutEffect(() => {
    const gen = ++genRef.current
    const forced = nonceRef.current !== reloadNonce
    nonceRef.current = reloadNonce
    cache.setOwner(owner)
    const startedAt = Date.now()
    const live = (): boolean => genRef.current === gen
    const commit = (patch: Partial<ViewState>): void => {
      if (!live()) return
      setView((v) => ({ ...v, ...patch, gen, rangeId: ident }))
    }

    const cSlot = forced ? undefined : cache.findCasevo(range, scope)
    const wantOutlook = connectedRef.current !== false
    const oSlot = forced || !wantOutlook ? undefined : cache.findOutlook(range)

    let casevo: CasevoBundle | null = cSlot ? cSlot.data : null
    let casevoKey = cSlot ? cSlot.key : null
    let casevoRange = cSlot ? cSlot.range : null
    let outlookReady = !wantOutlook || !!oSlot
    let outlookEvents: OutlookEvent[] = oSlot ? oSlot.data.events : []
    const fromCache = !!cSlot
    let revealed = false
    let failed = false

    const prefetch = (): void => {
      const { neighbours, scope: s } = latest.current
      for (const n of neighbours) {
        const c = cache.findCasevo(n, s)
        if (!c || !cache.isFresh(c)) void cache.loadCasevo(n, s).catch(() => undefined)
        if (connectedRef.current === true) {
          const o = cache.findOutlook(n)
          if (!o || !cache.isFresh(o)) void cache.loadOutlook(n).catch(() => undefined)
        }
      }
    }

    const tryReveal = (waited: boolean): void => {
      if (revealed || failed || !casevo || !live()) return
      // Wait for Outlook (up to waitMs) unless the rest came from the cache —
      // cached items show instantly.
      if (!outlookReady && !fromCache && !waited) return
      revealed = true
      commit({
        casevo, casevoKey, casevoRange,
        outlook: outlookReady ? outlookEvents : [],
        loading: false,
        outlookLoading: !outlookReady,
        outlookLate: !outlookReady,
        fromCache,
        error: null,
      })
      prefetch()
    }

    if (!casevo) {
      // Nothing for this range yet → grid + skeleton; never the old range's items.
      commit({ casevo: null, casevoKey: null, casevoRange: null, outlook: [], loading: true, outlookLoading: false, outlookLate: false, fromCache: false, error: null })
    }

    // Casevo: fetch when uncached, or quietly re-validate a stale cached copy
    // (re-fetching the covering range it came from, so that copy stays whole).
    if (!cSlot || !cache.isFresh(cSlot)) {
      const r = cSlot ? cSlot.range : range
      cache.loadCasevo(r, scope, { force: forced })
        .then((slot) => {
          if (!live()) return // navigated away — the cache keeps it for later
          if (!revealed) {
            casevo = slot.data; casevoKey = slot.key; casevoRange = slot.range
            tryReveal(Date.now() - startedAt >= waitMs)
          } else {
            commit({ casevo: slot.data, casevoKey: slot.key, casevoRange: slot.range })
          }
        })
        .catch((err) => {
          if (!live() || revealed) return // a quiet re-validate failing keeps what's shown
          failed = true
          // eslint-disable-next-line no-console
          console.error('[calendar] Failed to load calendar range:', err)
          commit({ casevo: null, loading: false, outlookLoading: false, error: LOAD_ERROR })
        })
    }

    // Outlook — in parallel with the above.
    if (wantOutlook && (!oSlot || !cache.isFresh(oSlot))) {
      cache.loadOutlook(oSlot ? oSlot.range : range, { force: forced })
        .then((r) => {
          if (!live()) return
          applyOutlookStatus(r.connected, r.needsReconnect)
          const events = r.connected ? r.events : []
          if (!outlookReady) {
            outlookReady = true
            outlookEvents = events
            if (revealed) commit({ outlook: events, outlookLoading: false })
            else tryReveal(false)
          } else if (revealed) {
            commit({ outlook: events }) // quiet re-validate of a cached copy
          } else {
            outlookEvents = events
          }
        })
        .catch((err) => {
          if (!live()) return
          // eslint-disable-next-line no-console
          console.error('[calendar] Outlook fetch failed:', err)
          if (!outlookReady) {
            outlookReady = true
            if (revealed) commit({ outlookLoading: false })
            else tryReveal(false)
          }
        })
    }

    // Everything cached → reveal now; else wait for data / the Outlook timer.
    tryReveal(false)
    const timer = window.setTimeout(() => tryReveal(true), waitMs)
    return () => window.clearTimeout(timer)
    // range/neighbours are derived from rangeId; scope/owner/nonce are in ident.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ident, reloadNonce])

  // The Microsoft status can land after the first range was revealed — once
  // it says "connected", prefetch the neighbours' Outlook too.
  useEffect(() => {
    if (outlookConnected !== true) return
    for (const n of latest.current.neighbours) {
      const o = cache.findOutlook(n)
      if (!o || !cache.isFresh(o)) void cache.loadOutlook(n).catch(() => undefined)
    }
    // Only on the connected flip (range changes prefetch via the load above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlookConnected])

  const setEntries = useCallback((fn: (es: CalendarEntry[] | null) => CalendarEntry[] | null): void => {
    setView((v) => {
      if (!v.casevo) return v
      const next = fn(v.casevo.entries)
      if (!next || next === v.casevo.entries) return v
      const casevo = { ...v.casevo, entries: next }
      if (v.casevoKey) cache.patchCasevo(v.casevoKey, casevo)
      return { ...v, casevo }
    })
  }, [cache])
  const setReminders = useCallback((fn: (rs: Reminder[]) => Reminder[]): void => {
    setView((v) => {
      if (!v.casevo) return v
      const next = fn(v.casevo.reminders)
      if (next === v.casevo.reminders) return v
      const casevo = { ...v.casevo, reminders: next }
      if (v.casevoKey) cache.patchCasevo(v.casevoKey, casevo)
      return { ...v, casevo }
    })
  }, [cache])

  const refresh = useCallback((o?: { outlook?: boolean }): void => {
    const gen = genRef.current
    const { range: r, scope: s } = latest.current
    const shown = viewRef.current
    cache.markAllStale()
    const target = shown.casevoRange ?? r
    const live = (): boolean => genRef.current === gen && latest.current.scope === s
    void cache.loadCasevo(target, s, { force: true })
      .then((slot) => {
        if (!live()) return
        setView((v) => (v.gen === gen ? { ...v, casevo: slot.data, casevoKey: slot.key, casevoRange: slot.range, loading: false, error: null } : v))
      })
      .catch((err) => {
        // Keep what's shown on a transient failure.
        // eslint-disable-next-line no-console
        console.error('[calendar] Quiet refresh failed:', err)
      })
    if (o?.outlook && connectedRef.current === true) {
      void cache.loadOutlook(r, { force: true, fresh: true })
        .then((res) => {
          if (!live()) return
          applyOutlookStatus(res.connected, res.needsReconnect)
          setView((v) => (v.gen === gen ? { ...v, outlook: res.connected ? res.events : [], outlookLoading: false } : v))
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[calendar] Outlook refresh failed:', err)
        })
    }
  }, [cache, applyOutlookStatus])

  // Until the effect for a NEW rangeId has run, never surface the old range.
  const current = view.rangeId === ident
  return {
    entries: current && view.casevo ? view.casevo.entries : null,
    notes: current && view.casevo ? view.casevo.notes : [],
    reminders: current && view.casevo ? view.casevo.reminders : [],
    outlookEvents: current ? view.outlook : [],
    loading: !current || view.loading,
    error: current ? view.error : null,
    outlookConnected,
    outlookNeedsReconnect,
    outlookLoading: current && view.outlookLoading && outlookConnected === true,
    outlookLate: current && view.outlookLate,
    fade: view.fromCache ? (view.outlookLate ? 'outlook' : 'none') : 'all',
    setEntries,
    setReminders,
    refresh,
  }
}
